import React, { useEffect, useState } from "react"
import "./App.css"
import "./components/styles/ficheVerrouillee.css"
import { useDispatch, useSelector } from "react-redux"
import { disconnectLogin, postLoginReset } from "./store/actions/navActions"
import {
	BrowserRouter as Router,
	Redirect,
	Route,
	Switch,
} from "react-router-dom"
import NavV2 from "./components/nav/NavV2"
import BandeauNouvelleVersion from "./components/nav/BandeauNouvelleVersion"
import Login from "./components/others/Login"
import ProtectedRoute from "./components/others/ProtectedRoute"
import Home from "./components/dashboard/Home"
import ClientFicheV2 from "./components/dashboard/clients/ClientFicheV2"
import ClientsTableV2 from "./components/dashboard/clients/ClientsTableV2"
import ParcV2 from "./components/dashboard/ParcV2"
import StatsV2 from "./components/dashboard/statsv2/StatsV2"
import EncaissementsV2 from "./components/dashboard/EncaissementsV2"
import Configuration from "./components/dashboard/configuration/Configuration"
import Facturation from "./components/dashboard/Facturation"
import Rapprochement from "./components/dashboard/Rapprochement"
import DepDispatchMenu from "./components/depannage/dispatch/DepDispatchMenu"
import DepFicheV2 from "./components/depannage/DepFicheV2"
import DepSearchV2 from "./components/depannage/DepSearchV2"
import DepResultatsV2 from "./components/depannage/DepResultatsV2"
import DepEtatV2 from "./components/depannage/DepEtatV2"
import InventaireV2 from "./components/atelier/inventaire/InventaireV2"
import AtelierEtatV2 from "./components/atelier/AtelierEtatV2"
import AtelierSearch from "./components/atelier/AtelierSearch"
import AtelierSearchV2 from "./components/atelier/AtelierSearchV2"
import AtelierResultatsV2 from "./components/atelier/AtelierResultatsV2"
import AtelierModeleListV2 from "./components/atelier/modele/AtelierModeleListV2"
import AtelierModeleFicheV2 from "./components/atelier/modele/AtelierModeleFicheV2"
import LocationV2 from "./components/location/LocationV2"
import LocationSearch from "./components/location/LocationSearch"
import LocationResultatsV2 from "./components/location/LocationResultatsV2"
import LocationGererV2 from "./components/location/LocationGererV2"
import EntretienV2 from "./components/entretien/EntretienV2"
import TachesV2 from "./components/entretien/TachesV2"
import NotificationsPage from "./components/notifications/NotificationsPage"
import ErreursPage from "./components/dashboard/ErreursPage"
import PatchnotesPage from "./components/dashboard/PatchnotesPage"
import { isTokenValid } from "./utilities/Auth"
import ForcePasswordChange from "./components/others/ForcePasswordChange"
import ChangePasswordPage from "./components/others/ChangePasswordPage"
import useTokenWatcher from "./hooks/auth"
import { useNotificationsSocket } from "./hooks/useNotificationsSocket"
import { useRights } from "./hooks/rights"
import { useAppBootstrap } from "./hooks/useAppBootstrap"
import Agenda from "./components/agenda/Agenda"
import AtelierWrapperV2 from "./components/atelier/AtelierWrapperV2"

export default function App() {
	useTokenWatcher()
	useNotificationsSocket()
	const dispatch = useDispatch()
	const { navInfo } = useSelector((state) => state)
	const { login } = navInfo
	const { token, mustChangePassword } = login
	const [windowDimensions, setWindowDimensions] = useState({
		width: window.innerWidth,
		height: window.innerHeight,
	})

	useEffect(() => {
		const handleResize = () => {
			const width = window.innerWidth
			const height = window.innerHeight

			setWindowDimensions({ width, height })
			document.body.innerHeight = height
		}
		// Écouter les changements de taille
		window.addEventListener("resize", handleResize)
		// Cleanup
		return () => {
			window.removeEventListener("resize", handleResize)
		}
	}, [])

	const isAuthenticated = () => {
		return isTokenValid(token)
	}

	// Préchargement des données de session (déplacé hors de la barre de nav).
	useAppBootstrap(isAuthenticated())

	const loginDisc = () => {
		dispatch(disconnectLogin()).then(() => {
			dispatch(postLoginReset())
		})
	}

	// Droits d'accès front centralisés (cf. hooks/rights.js).
	const { hasRight } = useRights()

	// Bloc B : 1re connexion → on bloque l'app sur l'écran de changement de mdp.
	if (isAuthenticated() && mustChangePassword) {
		return <ForcePasswordChange onLogout={loginDisc} />
	}

	return (
		<Router>
			<Switch>
				<>
					<div className="root-container">
						{isAuthenticated() && (
							<NavV2 loginDisconnect={loginDisc} />
						)}
						{isAuthenticated() && <BandeauNouvelleVersion />}
						<div
							className="main-container bg-2 hfull"
							style={{ padding: 0 }}
						>
							{/* Tableau de bord */}
							<Route exact path="/">
								<Login />
							</Route>
							<ProtectedRoute
								path={"/home"}
								component={Home}
								isAuthenticated={isAuthenticated()}
								hasRights={true}
							/>
							<ProtectedRoute
								path={"/clients"}
								component={ClientsTableV2}
								isAuthenticated={isAuthenticated()}
								hasRights={hasRight("clients")}
							/>
							<ProtectedRoute
								path={"/clients/:id"}
								component={ClientFicheV2}
								isAuthenticated={isAuthenticated()}
								hasRights={hasRight("clients")}
							/>
							<ProtectedRoute
								path={"/parc"}
								component={ParcV2}
								isAuthenticated={isAuthenticated()}
								hasRights={true}
							/>
							<ProtectedRoute
								path={"/stats"}
								component={StatsV2}
								isAuthenticated={isAuthenticated()}
								hasRights={hasRight("stats")}
							/>
							<ProtectedRoute
								path={"/configuration"}
								component={Configuration}
								isAuthenticated={isAuthenticated()}
								hasRights={hasRight("configuration")}
							/>
							<ProtectedRoute
								path={"/encaissements"}
								component={EncaissementsV2}
								isAuthenticated={isAuthenticated()}
								hasRights={hasRight("encaissements")}
							/>
							<ProtectedRoute
								path={"/facturation"}
								component={Facturation}
								isAuthenticated={isAuthenticated()}
								hasRights={hasRight("encaissements")}
							/>
							<ProtectedRoute
								path={"/rapprochement"}
								component={Rapprochement}
								isAuthenticated={isAuthenticated()}
								hasRights={hasRight("encaissements")}
							/>
							{/* Agenda */}
							<ProtectedRoute
								path={"/agenda"}
								component={Agenda}
								isAuthenticated={isAuthenticated()}
								hasRights={hasRight("agenda")}
							/>
							{/* Depannage */}
							<ProtectedRoute
								path={"/depannage/dispatch"}
								component={DepDispatchMenu}
								isAuthenticated={isAuthenticated()}
								hasRights={hasRight("dep_dispatch")}
							/>
							{/* Fiche V2 — le legacy (DepFiche.js) est mort avec
							    DepTransaction (chantier GE-21). */}
							<ProtectedRoute
								path={"/depannage/fiche/:id"}
								component={DepFicheV2}
								isAuthenticated={isAuthenticated()}
								hasRights={true}
							/>
							<ProtectedRoute
								path={"/depannage/resultats"}
								component={DepResultatsV2}
								isAuthenticated={isAuthenticated()}
								hasRights={true}
							/>
							<ProtectedRoute
								path={"/depannage/rechercher"}
								component={DepSearchV2}
								isAuthenticated={isAuthenticated()}
								hasRights={true}
							/>
							{/* GE-46 : menus gatés par groupe côté serveur
							    (DEPANNAGE_STATE_MENUS) ; A9 : la page au module
							    `dep_etat` (mêmes groupes). */}
							<ProtectedRoute
								path={"/depannage/etat"}
								component={DepEtatV2}
								isAuthenticated={isAuthenticated()}
								hasRights={hasRight("dep_etat")}
							/>
							{/* Atelier */}
							{/* La fiche legacy (Atelier.js, /atelier/fiche-legacy) est
							    supprimée le 29.07.2026 : la V2 est la seule fiche. */}
							<ProtectedRoute
								path={"/atelier/fiche/:id"}
								component={AtelierWrapperV2}
								isAuthenticated={isAuthenticated()}
								hasRights={hasRight("atelier")}
							/>
							<ProtectedRoute
								path={"/atelier/inventaire"}
								component={InventaireV2}
								isAuthenticated={isAuthenticated()}
								hasRights={hasRight("inventaire_page")}
							/>
							<ProtectedRoute
								path={"/atelier/etat"}
								component={AtelierEtatV2}
								isAuthenticated={isAuthenticated()}
								hasRights={hasRight("atelier")}
							/>
							{/* Recherche V2 en 2 pages (formulaire / résultats). Le legacy
							reste dispo sur /atelier/rechercher-legacy jusqu'à validation. */}
							{/* Recherche + résultats OUVERTS à tout authentifié (décision
							Jordan 31.07.2026, cf. navConfig) ; la FICHE reste gatée. */}
							<ProtectedRoute
								path={"/atelier/resultats"}
								component={AtelierResultatsV2}
								isAuthenticated={isAuthenticated()}
								hasRights={true}
							/>
							<ProtectedRoute
								path={"/atelier/rechercher-legacy"}
								component={AtelierSearch}
								isAuthenticated={isAuthenticated()}
								hasRights={hasRight("atelier")}
							/>
							<ProtectedRoute
								path={"/atelier/rechercher"}
								component={AtelierSearchV2}
								isAuthenticated={isAuthenticated()}
								hasRights={true}
							/>
							<ProtectedRoute
								path={"/atelier/modeles"}
								component={AtelierModeleListV2}
								isAuthenticated={isAuthenticated()}
								hasRights={hasRight("atelier_modeles")}
							/>
							<ProtectedRoute
								path={"/atelier/modeles/:id"}
								component={AtelierModeleFicheV2}
								isAuthenticated={isAuthenticated()}
								hasRights={hasRight("atelier_modeles")}
							/>
							{/* Location */}
							<ProtectedRoute
								path={"/location/fiche/:id"}
								component={LocationV2}
								isAuthenticated={isAuthenticated()}
								hasRights={hasRight("location")}
							/>
							{/* Recherche ouverte à tout authentifié (décision Jordan
							31.07.2026, cf. navConfig) ; la fiche reste gatée. */}
							{/* Recherche V2 en 2 pages (formulaire / résultats), comme
							l'atelier et le dépannage. */}
							<ProtectedRoute
								path={"/location/resultats"}
								component={LocationResultatsV2}
								isAuthenticated={isAuthenticated()}
								hasRights={true}
							/>
							<ProtectedRoute
								path={"/location/rechercher"}
								component={LocationSearch}
								isAuthenticated={isAuthenticated()}
								hasRights={true}
							/>
							<ProtectedRoute
								path={"/location/gerer"}
								component={LocationGererV2}
								isAuthenticated={isAuthenticated()}
								hasRights={hasRight("location")}
							/>
							{/* Entretiens (Jordan 27.10 : /entretien/gerer →
							    /entretiens, l'ancienne adresse redirige). */}
							<Route
								path="/entretien/gerer"
								render={() => <Redirect to="/entretiens" />}
							/>
							<ProtectedRoute
								path={"/entretiens"}
								component={EntretienV2}
								isAuthenticated={isAuthenticated()}
								hasRights={hasRight("entretiens")}
							/>
							<ProtectedRoute
								path={"/taches"}
								component={TachesV2}
								isAuthenticated={isAuthenticated()}
								hasRights={hasRight("taches")}
							/>
							{/* Ancienne URL (favoris, lien mobile) → nouvelle page.
							    ⚠ Scopé dans une Route : hors d'un Switch direct,
							    `from` est ignoré et un Redirect nu tire à CHAQUE
							    montage (tout F5 finissait sur /notifications). */}
							<Route
								path="/signalements"
								render={() => <Redirect to="/notifications" />}
							/>
							<ProtectedRoute
								path={"/notifications"}
								component={NotificationsPage}
								isAuthenticated={isAuthenticated()}
								hasRights={hasRight("notifications")}
							/>
							{/* Mon compte — accessible à tout utilisateur connecté
							    (menu compte de la nav). */}
							<ProtectedRoute
								path={"/compte/mot-de-passe"}
								component={ChangePasswordPage}
								isAuthenticated={isAuthenticated()}
								hasRights={true}
							/>
							<ProtectedRoute
								path={"/erreurs"}
								component={ErreursPage}
								isAuthenticated={isAuthenticated()}
								hasRights={hasRight("erreurs")}
							/>
							<ProtectedRoute
								path={"/patchnotes"}
								component={PatchnotesPage}
								isAuthenticated={isAuthenticated()}
								hasRights={hasRight("patchnotes")}
							/>
						</div>
					</div>
				</>
			</Switch>
		</Router>
	)
}
